import { BarElement, CategoryScale, Chart as ChartJS, LinearScale, Tooltip } from 'chart.js';
import moment from 'moment/moment';
import React, { useEffect, useRef, useState } from 'react';
import { Bar } from 'react-chartjs-2';



import arrowDownIcon from '../../../assets/img/icons/arrow-down.svg';
import {
  JobsStatusChartContainer,
  JobsStatusChartWrapper,
  JobsStatusContainer,
  TopPerformersHeader,
  TopPerformersSelectContainer,
  TopPerformersSelectIcon,
  TopPerformersSelectText,
  TopPerformersTitle,
  TopPerformersSkeletonTitle,
  TopPerformersSkeletonSelect,
  JobsStatusSkeletonChartContainer,
  JobsStatusSkeletonChartBars,
  JobsStatusSkeletonBar,
  JobsStatusSkeletonBarLabel,
} from "../../DashBoardNew/dashBoardNewStyles";


// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip
);

const JobsStatusBarChart = ({
  selectedOrganization,
  getJobInfoCustom,
  jobsCustomIsloading,
  jobsCustom,
  isOperationType,
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('Today');
  const [jobsData, setJobsData] = useState({ active: 0, pending: 0, done: 0, missed: 0 });
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const selectRef = useRef(null);
  const dropdownRef = useRef(null);

  // Call API on initial render and when selectedOrganization or selectedPeriod changes
  useEffect(() => {
    if (selectedOrganization?.id && getJobInfoCustom) {
      let startTime, endTime;

      if (selectedPeriod === 'Today') {
        startTime = moment().format('YYYY-MM-DD');
        endTime = moment().format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This Week') {
        startTime = moment().startOf('week').format('YYYY-MM-DD');
        endTime = moment().endOf('week').format('YYYY-MM-DD');
      } else if (selectedPeriod === 'This Month') {
        startTime = moment().startOf('month').format('YYYY-MM-DD');
        endTime = moment().endOf('month').format('YYYY-MM-DD');
      }

      const payload = {
        organization_id: selectedOrganization.id,
        start_time: startTime,
        end_time: endTime,
      };

      getJobInfoCustom(payload);
    }
  }, [selectedOrganization, selectedPeriod, getJobInfoCustom]);

  // Update jobsData when jobsCustom changes
  useEffect(() => {
    if (jobsCustom && Object.keys(jobsCustom).length) {
      // Map API response to chart format
      setJobsData({
        active: jobsCustom.in_progress_jobs || 0,
        pending: jobsCustom.pending_jobs || 0,
        done: jobsCustom.completed_jobs || 0,
        missed: jobsCustom.missed_jobs || 0,
      });
    }
  }, [jobsCustom]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsSelectOpen(false);
      }
    };

    if (isSelectOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSelectOpen]);

  const handleSelectClick = () => {
    setIsSelectOpen(!isSelectOpen);
  };

  const handlePeriodSelect = (period) => {
    setIsSelectOpen(false);
    setSelectedPeriod(period);
  };

  const chartValues = [jobsData.active, jobsData.pending, jobsData.done, jobsData.missed];
  const chartLabels = ['In Progress', 'Pending', 'Completed', 'Missed'];
  const maxValue = Math.max(...chartValues, 0);
  const paddedMax = maxValue === 0 ? 20 : maxValue * 1.1;
  const maxAxisValue = Math.ceil(paddedMax / 10) * 10;
  const stepSize = Math.max(Math.round(maxAxisValue / 5), 1);

  // Chart data configuration - matches Figma design exactly
  const chartData = {
    labels: chartLabels,
    datasets: [
      {
        label: isOperationType ? 'Tasks' : 'Jobs',
        data: chartValues,
        backgroundColor: [
          '#cbd5e1', // Active - gray
          '#ffd699', // Pending - orange
          '#8db1f7', // Done - blue
          '#fda4af', // Missed - pink
        ],
        borderRadius: 0,
        maxBarThickness: 48,
        categoryPercentage: 0.92,
        barPercentage: 0.95,
      },
    ],
  };

  // Custom plugin to display values on top of bars
  const valueLabelPlugin = {
    id: 'valueLabel',
    afterDatasetsDraw: (chart) => {
      const { ctx, data, scales } = chart;
      ctx.save();
      ctx.font = '400 12px Mona Sans, sans-serif';
      ctx.fillStyle = '#353a46';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      data.datasets.forEach((dataset, datasetIndex) => {
        dataset.data.forEach((value, index) => {
          const meta = chart.getDatasetMeta(datasetIndex);
          const bar = meta.data[index];
          if (bar) {
            const x = bar.x;
            const y = bar.y;
            ctx.fillText(value.toString(), x, y - 4);
          }
        });
      });

      ctx.restore();
    },
  };

  // Chart options - matches Hourly chart axis alignment
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: false,
      },
    },
    scales: {
      x: {
        border: {
          display: true,
          color: '#d3e0fc',
          width: 2,
        },
        grid: {
          display: false,
        },
        ticks: {
          color: '#353a46',
          font: {
            size: 12,
          },
        },
      },
      y: {
        border: {
          display: true,
          color: '#d3e0fc',
          width: 2,
        },
        min: 0,
        max: maxAxisValue,
        grid: {
          color: '#e9f0fe',
          lineWidth: 1,
          drawTicks: false,
        },
        ticks: {
          color: '#666e7f',
          stepSize,
          font: {
            size: 12,
          },
          callback: (value) => value,
        },
        beginAtZero: true,
      },
    },
    layout: {
      padding: {
        top: 12,
        bottom: 0,
        left: 0,
        right: 0,
      },
    },
  };

  // Render skeleton loading state
  if (jobsCustomIsloading) {
    return (
      <JobsStatusContainer>
        <TopPerformersHeader>
          <TopPerformersSkeletonTitle />
          <TopPerformersSkeletonSelect />
        </TopPerformersHeader>

        <JobsStatusSkeletonChartContainer>
          <JobsStatusSkeletonChartBars>
            {[1, 2, 3, 4].map((index) => (
              <div key={`skeleton-bar-${index}`} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', maxWidth: '48px' }}>
                <JobsStatusSkeletonBar
                  style={{
                    height: `${40 + index * 20}%`,
                  }}
                />
                <JobsStatusSkeletonBarLabel />
              </div>
            ))}
          </JobsStatusSkeletonChartBars>
        </JobsStatusSkeletonChartContainer>
      </JobsStatusContainer>
    );
  }

  return (
    <JobsStatusContainer>
      <TopPerformersHeader>
        <TopPerformersTitle>{isOperationType ? 'Tasks' : 'Jobs'}</TopPerformersTitle>
        <TopPerformersSelectContainer ref={selectRef} onClick={handleSelectClick}>
          <TopPerformersSelectText>{selectedPeriod}</TopPerformersSelectText>
          <TopPerformersSelectIcon src={arrowDownIcon} alt="Dropdown" />
          {isSelectOpen && (
            <div
              ref={dropdownRef}
              style={{
                position: 'absolute',
                top: '100%',
                right: 0,
                marginTop: '4px',
                background: '#ffffff',
                border: '1px solid #d3e0fc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                zIndex: 100,
                minWidth: '100%',
              }}
            >
              {['Today', 'This Week', 'This Month'].map((option) => (
                <div
                  key={option}
                  onClick={() => handlePeriodSelect(option)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontFamily: 'Mona Sans, sans-serif',
                    fontSize: '12px',
                    color: '#353a46',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#f3f5f9';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                  }}
                >
                  {option}
                </div>
              ))}
            </div>
          )}
        </TopPerformersSelectContainer>
      </TopPerformersHeader>

      <JobsStatusChartContainer>
        <JobsStatusChartWrapper>
          <Bar
            data={chartData}
            options={chartOptions}
            plugins={[valueLabelPlugin]}
          />
        </JobsStatusChartWrapper>
      </JobsStatusChartContainer>
    </JobsStatusContainer>
  );
};

export default JobsStatusBarChart;